import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  ModalBody,
  ModalContent,
  ModalFooter,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { type TezosToolkit } from "@taquito/taquito";
import { type ChainFullId, getHash, tezToMutez } from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { useCurrentAccount } from "@umami/state";
import { FormProvider, useForm } from "react-hook-form";

import { type ShieldFormValues } from "./types";
import { useSubmitShield } from "./useSubmitShield";
import { AddressTile } from "../../AddressTile/AddressTile";
import { TezTile } from "../../AssetTiles/TezTile";
import { AsyncSignPageFee } from "../../SendFlow/SignPageFee";
import { SignPageHeader } from "../../SendFlow/SignPageHeader";
import { SuccessStep } from "../../SendFlow/SuccessStep";
import { ToAddressPreviewField } from "../SendShieldedFlow/form/ToAddressPreviewField";

import { FormSubmitButton } from "@/components/SendFlow/utils";
import { useColor } from "@/styles/useColor";

type SignPageProps = {
  formValues: Omit<ShieldFormValues, "password">;
  fee: number;
  toolkit: TezosToolkit;
  chainId: ChainFullId;
  shieldedTx: string;
};

export const SignPage = ({ formValues, fee, toolkit, chainId, shieldedTx }: SignPageProps) => {
  const { openWith } = useDynamicModalContext();
  const currentAccount = useCurrentAccount();
  const { submitShield, isLoading } = useSubmitShield({
    amount: Number(formValues.amount),
    shieldedTx,
    toolkit,
  });
  const hideBalance = useBreakpointValue({ base: true, md: false });
  const form = useForm();
  const color = useColor();

  const handleSubmit = async () => {
    const result = await submitShield();

    const hash = typeof result === "string" ? result : getHash(result);

    await openWith(<SuccessStep chainId={chainId} hash={hash} />);
  };

  if (!currentAccount) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <SignPageHeader title="Confirm Shield" />
          <ModalBody gap="24px">
            <FormControl>
              <FormLabel width="full">Amount</FormLabel>
              <TezTile chainId={chainId} mutezAmount={tezToMutez(formValues.amount).toString()} />
              <Flex justifyContent="end" width="full" marginTop="12px">
                <AsyncSignPageFee chainId={chainId} fee={fee} />
              </Flex>
            </FormControl>
            <FormControl>
              <FormLabel width="full">From</FormLabel>
              <AddressTile address={currentAccount.address} hideBalance={hideBalance} />
            </FormControl>
            <ToAddressPreviewField address={formValues.shieldedAddress} />
            {formValues.memo && (
              <FormControl>
                <FormLabel width="full">Memo</FormLabel>
                <Box
                  padding="12px 16px"
                  background={color("100")}
                  borderRadius="4px"
                  data-testid="memo-tile"
                >
                  <Text color={color("700")} fontSize="14px">
                    {formValues.memo}
                  </Text>
                </Box>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <FormSubmitButton isLoading={isLoading} title="Submit shield" />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
